/**
 * Polish voivodeship (województwo) constants for region filtering.
 * Provides mapping between URL-friendly slugs and display labels.
 *
 * NOTE: These constants are primarily used in tests and as a reference.
 * Runtime UI uses labels from the API response (rc.label from getRegionCounts).
 *
 * @see docs/specifications/2026-02-26-grants-sources-region-filter.md
 */

/**
 * Mapping of region slugs (lowercase, no diacritics) to display labels (with proper Polish characters).
 * Used for region filter dropdown on /grants/sources page.
 */
export const REGION_LABELS = {
  dolnoslaskie: 'Dolnośląskie',
  'kujawsko-pomorskie': 'Kujawsko-Pomorskie',
  lubelskie: 'Lubelskie',
  lubuskie: 'Lubuskie',
  lodzkie: 'Łódzkie',
  malopolskie: 'Małopolskie',
  mazowieckie: 'Mazowieckie',
  opolskie: 'Opolskie',
  podkarpackie: 'Podkarpackie',
  podlaskie: 'Podlaskie',
  pomorskie: 'Pomorskie',
  slaskie: 'Śląskie',
  swietokrzyskie: 'Świętokrzyskie',
  'warminsko-mazurskie': 'Warmińsko-Mazurskie',
  wielkopolskie: 'Wielkopolskie',
  zachodniopomorskie: 'Zachodniopomorskie',
} as const;

/**
 * Type for region slug (URL-friendly identifier).
 */
export type RegionSlug = keyof typeof REGION_LABELS;

/**
 * Array of all valid region slugs.
 * Useful for iteration or validation.
 */
export const VALID_REGIONS = Object.keys(REGION_LABELS) as RegionSlug[];

/**
 * Mapping from Polish diacritic variants to canonical ASCII slugs.
 * Used to normalize region values that may come with diacritics from DB.
 *
 * NOTE: Duplicated in apps/backend/src/grants/shared/region.constants.ts.
 * Shared packages exist (packages/*, see ADR-074/075), but this map has not
 * been moved there yet. Keep both copies in sync.
 */
const DIACRITIC_TO_SLUG: Record<string, RegionSlug> = {
  'dolnośląskie': 'dolnoslaskie',
  'łódzkie': 'lodzkie',
  'małopolskie': 'malopolskie',
  'śląskie': 'slaskie',
  'świętokrzyskie': 'swietokrzyskie',
  'warmińsko-mazurskie': 'warminsko-mazurskie',
};

/**
 * Normalizes a region string to canonical ASCII slug format.
 * Returns lowercased/trimmed input if it doesn't match any known region.
 */
export function normalizeRegion(region: string): string {
  const lower = region.toLowerCase().trim();
  // hasOwnProperty, not `in`: "constructor" & co. must not resolve via the prototype
  if (Object.prototype.hasOwnProperty.call(REGION_LABELS, lower)) return lower;
  if (Object.prototype.hasOwnProperty.call(DIACRITIC_TO_SLUG, lower)) return DIACRITIC_TO_SLUG[lower];
  return lower;
}

/**
 * Gets the display label for a region slug. Returns the slug as fallback.
 */
export function getRegionLabel(region: string): string {
  return (REGION_LABELS as Record<string, string>)[normalizeRegion(region)] || region;
}
